import { Injectable } from '@angular/core';
import { BasicInsuranceModel, BasicInsurancePlan, GenderOrPrenatal } from '../../../api';
import { TrackingService } from '../tracking.service';
import { ECOMMERCE_EVENT_TYPES, Item } from './tracking-ecommerce.events';
import { SupplementaryInsurancesData } from '../../../../pages/supplementary-insurance/supplementary-insurance-selection-page/supplementary-insurance.mapper';
import { CURRENCY_CHF } from './tracking-ecommerce.map';
import { SupplementaryInsuranceTypes } from '../../../states/offer/offer.state';
import {
  mapBasicInsuranceDetailItemFromModel,
  mapBasicInsuranceItem,
  mapBasicInsuranceItemByModel,
  mapBasicInsuranceVariantItem,
} from './tracking-ecommerce-basic-insurance.map';
import {
  mapSupplementaryInsuranceItem,
  mapSupplementaryInsuranceItems,
  mapSupplementaryInsurancesOfProduct,
  mapSupplementaryInsuranceVariantItem,
} from './tracking-ecommerce-supplementary-insurance.map';

@Injectable({
  providedIn: 'root',
})
export class TrackingEcommerceService {
  public trackViewItemListEvent(basicInsurancePlans: BasicInsurancePlan[]): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.VIEW_ITEM_LIST,
      step: 'step_3',
      premium_calculator: 'product_list_impression_calculator_kvg',
      ecommerce: {
        items: basicInsurancePlans.map((basicInsurancePlan, index) => {
          return mapBasicInsuranceItem(basicInsurancePlan, index);
        }),
      },
    });
  }

  public trackSelectItemEvent(basicInsurancePlan: BasicInsurancePlan): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.SELECT_ITEM,
      step: 'step_4',
      premium_calculator: 'select_product_kvg',
      ecommerce: {
        items: [mapBasicInsuranceItem(basicInsurancePlan, 0)],
      },
    });
  }

  public trackViewItemEvent(basicInsurancePlan: BasicInsurancePlan): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.VIEW_ITEM,
      step: 'step_5',
      premium_calculator: 'view_product_kvg',
      ecommerce: {
        items: basicInsurancePlan.models.map((basicInsuranceModel, index) => {
          return mapBasicInsuranceVariantItem(basicInsuranceModel, index);
        }),
      },
    });
  }

  public addToCartEvent(
    basicInsuranceModel: BasicInsuranceModel,
    franchise: string,
    includesAccidentCoverage: boolean,
    totalPersons: number,
    gender: GenderOrPrenatal,
    existingCustomer: boolean,
  ): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.ADD_TO_CART,
      step: 'step_6',
      premium_calculator: 'select_basic_coverage',
      is_existing_customer: existingCustomer,
      ecommerce: {
        items: [
          mapBasicInsuranceDetailItemFromModel(
            basicInsuranceModel,
            franchise,
            includesAccidentCoverage,
            totalPersons,
            gender,
          ),
        ],
      },
    });
  }

  public trackSupplementaryInsuranceViewItemListEvent(supplementaryInsuranceData: SupplementaryInsurancesData): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.VIEW_ITEM_LIST,
      step: 'step_7',
      premium_calculator: 'product_list_impression_calculator_vvg',
      ecommerce: {
        items: mapSupplementaryInsuranceItems(supplementaryInsuranceData),
      },
    });
  }

  public trackSupplementaryInsuranceSelectItemListEvent(
    product: SupplementaryInsuranceTypes,
    supplementaryInsuranceData: SupplementaryInsurancesData,
  ): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.SELECT_ITEM,
      step: 'step_8',
      premium_calculator: 'select_product_vvg',
      ecommerce: {
        items: [mapSupplementaryInsuranceItem(product, supplementaryInsuranceData[`${product}LowestPrice`] as number)],
      },
    });
  }

  public trackSupplementaryInsuranceViewItemEvent(
    product: SupplementaryInsuranceTypes,
    supplementaryInsuranceData: SupplementaryInsurancesData,
  ): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.VIEW_ITEM,
      step: 'step_9',
      premium_calculator: 'view_product_vvg',
      ecommerce: {
        items: mapSupplementaryInsurancesOfProduct(product, supplementaryInsuranceData),
      },
    });
  }

  public trackSupplementaryInsuranceAddToCartEvent(product: Item, existingCustomer: boolean): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.ADD_TO_CART,
      step: 'step_10',
      premium_calculator: 'select_additional_coverage',
      is_existing_customer: existingCustomer,
      ecommerce: {
        items: [product],
      },
    });
  }

  public trackViewCartEvent(summaryPrice: number, isExistingCustomer: boolean, products: Item[]): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.VIEW_CART,
      step: 'step_11',
      premium_calculator: 'view_cart',
      is_existing_customer: isExistingCustomer,
      ecommerce: {
        currency: CURRENCY_CHF,
        value: summaryPrice,
        items: products,
      },
    });
  }

  public trackBeginCheckout(products: Item[]): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.BEGIN_CHECKOUT,
      step: 'step_12',
      premium_calculator: 'begin_checkout_purchase',
      ecommerce: {
        items: products,
      },
    });
  }

  public trackOrderOfferBeginCheckout(products: Item[]): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.BEGIN_CHECKOUT,
      step: 'step_12',
      premium_calculator: 'begin_checkout_offer',
      ecommerce: {
        items: products,
      },
    });
  }

  public trackShippingInfo(processId: string, products: Item[]): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.ADD_SHIPPING_INFO,
      step: 'step_13',
      premium_calculator: 'personal_data',
      ecommerce: {
        transaction_id: processId,
        shipping_tier: 'Standard',
        items: products,
      },
    });
  }

  public trackCalculatorEvent(summaryPrice: number, products: Item[]): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.CALCULATOR,
      item_category: 'Krankenversicherung',
      purchase_type: 'offer',
      step: 'step_13',
      premium_calculator: 'offer_confirm',
      ecommerce: {
        value: summaryPrice,
        items: products,
      },
    });
  }

  public trackBasicInsuranceRemoveFromCartEvent(basicInsuranceModel: BasicInsuranceModel): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.REMOVE_FROM_CART,
      ecommerce: {
        items: [mapBasicInsuranceItemByModel(basicInsuranceModel, 0)],
      },
    });
  }

  public trackSupplementaryInsuranceRemoveFromCartEvent(product: SupplementaryInsuranceTypes, price: number): void {
    TrackingService.trackEvent({
      event: ECOMMERCE_EVENT_TYPES.REMOVE_FROM_CART,
      ecommerce: {
        items: [mapSupplementaryInsuranceVariantItem(product, price)],
      },
    });
  }
}
